import React, { useRef, useEffect } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import _ from "lodash";
import moment from "moment";
import Swal from "sweetalert2";

const UsageRightRenderingRelease = (props) => {
  console.log("UsageRight Rendering Release Props: ", props);
  const {
    hotelName = props.photoshootDetails.hotel_name ||
      "____________________________",
    propertyCode = props.photoshootDetails.marsha ||
      "____________________________",
    photographerName = props.photoshootDetails.photographer_name ||
      "____________________________",
    architectCompanyName = props.photoshootDetails.ph_name ||
      "_________________________________________________________________________",
    architectEmail = props.photoshootDetails.architect_email ||
      "____________________________",
    architectPhone = props.photoshootDetails.architect_phone ||
      "____________________________",
    signatureDate = "____________________________",
    printName = "____________________________",
    title = "____________________________",
    photoshootImages = [],
  } = props;

  const textRef = useRef(null);
  const photosRef = useRef(null);
  const hasRun = useRef(false);

  const capture = async (node) => {
    if (!node) return null;
    const images = node.querySelectorAll("img");
    await Promise.all(
      [...images].map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((res) => {
          img.onload = img.onerror = res;
        });
      }),
    );

    await new Promise((res) => setTimeout(res, 200));

    return html2canvas(node, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      letterRendering: true,
    });
  };

  const chunkImages = (arr, size = 16) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const generatePDF = async () => {
    try {
      if (!textRef.current) {
        console.error("textRef not found");
        return;
      }
      const pdf = new jsPDF("p", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      /* ================= PAGE 1 — TEXT ================= */
      const textCanvas = await capture(textRef.current);
      const textImg = textCanvas.toDataURL("image/png");
      const textHeight = (textCanvas.height * pageWidth) / textCanvas.width;

      pdf.addImage(
        textImg,
        "PNG",
        0,
        0,
        pageWidth,
        Math.min(textHeight, pageHeight),
      );

      /* ================= IMAGE PAGES ================= */
      const approvedPhotos = photoshootImages.filter(
        (p) => p.status === "bt-approved",
      );
      //const approvedPhotos = _.times(24, () => photoshootImages.filter((p) => p.status === "bt-approved")).flat();
      const imagePages = chunkImages(approvedPhotos, 16);

      for (let i = 0; i < imagePages.length; i++) {
        pdf.addPage();

        // Inject ONLY 12 images into DOM
        photosRef.current.innerHTML = "";
        imagePages[i].forEach((img) => {
          const wrapper = document.createElement("div");
          wrapper.innerHTML = `
        <div style="text-align:center">
          <img src="${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${img.fname}"
               style="width:180px;height:180px;object-fit:contain"/>
          <div style="font-size:10px;word-break:break-all">${img.fname}</div>
        </div>
      `;
          photosRef.current.appendChild(wrapper);
        });

        /* ✅ ADD FOOTER FOR EACH IMAGE PAGE */
        /* const footer = document.createElement("p");
        footer.style.textAlign = "right";
        footer.style.marginTop = "8px";
        footer.style.fontSize = "8px";
        footer.innerHTML = `© ${printName} / Nacása &amp; Partners Inc.`;

        photosRef.current.appendChild(footer);
        */
        await new Promise((res) => setTimeout(res, 300));
        const canvas = await capture(photosRef.current);
        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * pageWidth) / canvas.width;

        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pageWidth,
          Math.min(imgHeight, pageHeight),
        );
      }
      //props.resetDownload();
      //window.open(pdf.output("bloburl"), "_blank");
      pdf.save("usage-rights.pdf");
    } catch (err) {
      console.error("PDF generation failed:", err);

      Swal.fire({
        title: "Error",
        text: "Failed to generate PDF",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    if (hasRun.current) return;

    const timer = setTimeout(() => {
      Swal.fire({
        title: "Download Usage Rights",
        text: "Do you want to generate and download Usage Rights PDF?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Download",
        cancelButtonText: "Cancel",
      }).then(async (result) => {
        if (result.isConfirmed) {
          hasRun.current = true;

          Swal.fire({
            title: "Generating PDF...",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });

          await new Promise((res) => setTimeout(res, 500));
          await generatePDF();

          Swal.close();
          props.resetDownload();
        } else {
          props.resetDownload();
        }
      });
    }, 300); // ensures DOM ready

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      {/* ================= PAGE 1 : TEXT ================= */}
      <div
        ref={textRef}
        className="bg-white text-black p-10"
        style={{ width: "794px", fontSize: "14px" }}
      >
        {/*<div className="text-center mb-5" style={{ fontSize: "8px", marginTop: "-20px" }}>
          MARRIOTT CONFIDENTIAL AND PROPRIETARY INFORMATION
        </div>*/}
        <div className="flex flex-col items-center my-2">
          <img
            src={process.env.PUBLIC_URL + "/images/marriott-logo.png"}
            alt="Marriott Logo"
            className="h-12 mb-2"
          />
        </div>

        <h1 className="text-[18px] text-center mb-5 uppercase font-bold text-red-600">
          License of Unlimited Usage Rights for Renderings
        </h1>

        <div className="mb-5 grid grid-cols-2 gap-x-1 gap-y-1">
          <div className="col-span-1">Hotel Name: {hotelName}</div>
          <div className="col-span-1">
            Property/MARSHA Code:: {propertyCode}
          </div>
          <div className="col-span-2">
            Architect Company Name: {architectCompanyName}
          </div>
          <div className="col-span-1">Architect’s Email: {architectEmail}</div>
          <div className="col-span-1">
            Architect’s Phone Number: {architectPhone}
          </div>
        </div>

        <div className="mb-5">
          <p>
            The Architect represents and warrants that: (1) each person who
            created the attached design rendering(s) (together, the “Works”) is
            an employee or sub-contractor of the Architect and the sole
            creator(s) of the Works; (2) each sub-contractor who created the
            Works has executed a written assignment of all rights, including the
            copyright, in the Works; and (3) the Architect is the sole owner of
            the copyright in the Works; and (3) the use of the Works by Marriott
            and its designees will not violate the rights of any third party.
          </p>
        </div>

        <div className="mb-5">
          <p>
            In exchange for valuable consideration, the sufficiency and receipt
            of which is acknowledged, Architect grants to Marriott
            International, Inc., its affiliates, the hotel owner and, if the
            hotel is franchised, the hotel franchisee (collectively, “Marriott”)
            a perpetual, non-exclusive, irrevocable worldwide license to
            distribute, publicly display, and create derivative works of the
            Works in all media now known or hereafter developed and to
            sublicense the foregoing rights to third parties; provided, however,
            that Marriott may not resell the Works for profit. No further
            permission is required for Marriott and its designees to reproduce
            or otherwise use the Works for any marketing purpose in any medium,
            without credit or attribution, including without limitation, the
            following examples:
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>
              for marketing materials, advertising and promotions in all
              channels and at all levels (including digital and online
              directories, electronic media/brochures, video, print, brochures,
              publications, newsletters, guides, direct mail, point-of-sale and
              vehicle graphics);
            </li>
            <li>
              for all digital formats, media, and social media sites such as
              YouTube (including display graphics for television, or other
              display at any location or channel such as hotel properties, trade
              shows and airport dioramas).
            </li>
            <li>
              for all Internet and Intranet sites, whether owned or controlled
              by Marriott or by its affiliates and franchisees, or by unrelated
              third parties including without limitation Online Travel Agencies
              such as expedia.com or hotels.com
            </li>
            <li>for prints, file replication and framed photographs.</li>
          </ul>
        </div>

        <div className="mb-5">
          <p>
            In the event of a conflict between the terms of this document and
            the terms of any other document, the terms of this document shall
            prevail.
          </p>
        </div>

        <div className="mb-5">
          <p>
            The person signing below represents and warrants that he/she is
            authorized to execute this letter on behalf of the Architect.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2">
            <div className="col-span-1">
              Signature: ____________________________
            </div>
            <div className="col-span-1 text-right">Print Name: {printName}</div>
            <div className="col-span-1">Date: {signatureDate}</div>
            <div className="col-span-1 text-right">Title: {title}</div>
          </div>
        </div>

        <div className="mt-8">
          <p
            className="mb-2 font-bold text-red-600"
            style={{ fontSize: "11px" }}
          >
            <i>
              *A contact sheet of all Work(s) must accompany this license. This
              letter must be signed and dated by an authorized representative of
              the Architect* 20-dec-17
            </i>
          </p>

          <div className="my-5"></div>
        </div>

        {/* PHOTOS PAGE */}
        <div
          ref={photosRef}
          style={{
            width: "794px",
            padding: "40px",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "16px",
            background: "#fff",
          }}
        ></div>
      </div>
    </div>
  );
};

export default UsageRightRenderingRelease;
